import React, { useState, useEffect, useCallback, memo, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  CircularProgress,
  Alert,
  Collapse,
  Autocomplete,
  Divider,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Switch,
  FormControlLabel,
  Tooltip,
  Paper,
  Grid,
  Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useSamples, invalidateSampleCache, deleteSample } from '../Lookup_components/Sample_lookup';
import { useDomainValues } from '../Lookup_components/document_domainvalueslookup';
import debounce from 'lodash/debounce';
import { supabase } from '../../supabaseClient';
import { v4 as uuidv4 } from 'uuid';
import ClearIcon from '@mui/icons-material/Clear';
import Draggable from 'react-draggable';

// Draggable Dialog Component
function DraggableDialog(props) {
  const nodeRef = useRef(null);
  return (
    <Draggable nodeRef={nodeRef} handle="#bulk-create-dialog-title">
      <Paper ref={nodeRef} {...props} />
    </Draggable>
  );
}

// Add this after the existing DraggableDialog component
function BulkDraggableDialog(props) {
  const nodeRef = useRef(null);
  return (
    <Draggable nodeRef={nodeRef} handle="#bulk-create-dialog-title">
      <Paper ref={nodeRef} {...props} />
    </Draggable>
  );
}

// Autocomplete component remains unchanged
const AutocompleteField = memo(({ label, value, onChange, options, loading, keyPrefix, isReadOnly }) => {
  const [open, setOpen] = useState(false);
  
  // Special handling for boreholes which have a different structure
  const isBoreholeField = label.toLowerCase().includes('boring');
  
  // Find current value based on field type
  const currentOption = isBoreholeField 
    ? options?.find(option => option.id === value) 
    : options?.find(option => option.domain_value_id === value) || null;

  // Sort options alphabetically, except for unit fields and boreholes
  const sortedOptions = useMemo(() => {
    if (!options || !Array.isArray(options)) return [];
    
    // Don't sort if the label contains "eenheid" or "boring" (case insensitive)
    const isUnitField = label.toLowerCase().includes('eenheid');
    
    if (isUnitField || isBoreholeField) {
      return options;
    }
    
    return [...options].sort((a, b) => 
      (a.description || '').localeCompare(b.description || '')
    );
  }, [options, label, isBoreholeField]);

  return (
    <Autocomplete
      fullWidth
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={sortedOptions}
      getOptionLabel={(option) => isBoreholeField ? (option.name || 'Naamloos') : (option.description || '')}
      value={currentOption}
      onChange={(event, newValue) => {
        onChange(isBoreholeField ? newValue?.id : newValue?.domain_value_id || null);
      }}
      disabled={isReadOnly}
      renderInput={(params) => (
        <TextField 
          {...params} 
          label={label} 
          fullWidth
          margin="normal"
          required={label.includes('(verplicht)')}
        />
      )}
      loading={loading}
      loadingText="Opties laden..."
      noOptionsText="Geen resultaten"
      isOptionEqualToValue={(option, value) => 
        isBoreholeField 
          ? option?.id === value?.id
          : option?.domain_value_id === value?.domain_value_id
      }
      renderOption={(props, option) => (
        <li {...props} key={`${keyPrefix}-${isBoreholeField ? option.id : option.domain_value_id}-${isBoreholeField ? option.name : option.description}`}>
          {isBoreholeField ? option.name : option.description}
        </li>
      )}
    />
  );
});

// --- VeldmonsterForm Component ---
const VeldmonsterForm = memo(({
  sample,
  boreholes,
  analysemonsters,
  domainValues,
  domainValuesLoading,
  lengthUnitsData,
  lengthUnitsLoading,
  referentieVlakData,
  referentieVlakLoading,
  handleVeldmonsterChange,
  isReadOnly
}) => {
  return (
    <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
      {/* Naam Veldmonster */}
      <TextField
        label="Naam Veldmonster"
        value={sample.name || ''}
        onChange={(e) => handleVeldmonsterChange(sample.id, 'name', e.target.value)}
        fullWidth
        margin="normal"
        disabled={isReadOnly}
      />


      {/* Startdatum */}
      <DatePicker
        label="Startdatum"
        value={sample.starttime ? new Date(sample.starttime) : null}
        onChange={(newValue) => {
          // FIX: Only set ISO string if valid date, else set null
          const validDate = newValue instanceof Date && !isNaN(newValue);
          setBulkCreateData(prev => ({ ...prev, starttime: validDate ? newValue.toISOString() : null }));
        }}
        format="dd-MM-yyyy"
        disabled={isReadOnly}
        slotProps={{
          textField: {
            fullWidth: true,
            margin: "normal",
            InputProps: {
              endAdornment: (
                <>
                  {sample.starttime && (
                    <InputAdornment position="end" sx={{ marginRight: -1.5 }}>
                      <IconButton
                        aria-label="clear date"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVeldmonsterChange(sample.id, 'starttime', null);
                        }}
                        edge="end"
                        size="small"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )}
                </>
              ),
            }
          }
        }}
      />

      {/* Veld matrix (materialclass) */}
      <AutocompleteField
        label="Veld matrix"
        value={sample.materialclass}
        onChange={(newValue) => handleVeldmonsterChange(sample.id, 'materialclass', newValue)}
        options={domainValues['Compartiment']}
        loading={domainValuesLoading}
        keyPrefix={`matrix-${sample.id}`}
        isReadOnly={isReadOnly}
      />

      {/* Bovendiepte + Eenheid + Conditie */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
        <TextField
          label="Bovendiepte"
          type="number"
          inputProps={{ inputMode: 'decimal', step: 'any' }}
          value={sample.upperdepth ?? ''}
          onChange={(e) => handleVeldmonsterChange(sample.id, 'upperdepth', e.target.value ? parseFloat(e.target.value) : null)}
          fullWidth
          margin="normal"
          sx={{ flex: 1 }}
          disabled={isReadOnly}
        />
        <Box sx={{ flex: 1 }}>
          <AutocompleteField
            label="Eenheid"
            value={sample.upperdepth_uom}
            onChange={(newValue) => handleVeldmonsterChange(sample.id, 'upperdepth_uom', newValue)}
            options={lengthUnitsData?.['Eenheid'] || []}
            loading={lengthUnitsLoading}
            keyPrefix={`upperdepth-uom-${sample.id}`}
            isReadOnly={isReadOnly}
          />
        </Box>
      </Box>
      <AutocompleteField
        label="Bovendiepte Conditie"
        value={sample.upperdepth_condition}
        onChange={(newValue) => handleVeldmonsterChange(sample.id, 'upperdepth_condition', newValue)}
        options={referentieVlakData?.['Hoedanigheid'] || []}
        loading={referentieVlakLoading}
        keyPrefix={`upperdepth-cond-${sample.id}`}
        isReadOnly={isReadOnly}
      />

      {/* Onderdiepte + Eenheid + Conditie */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
        <TextField
          label="Onderdiepte"
          type="number"
          inputProps={{ inputMode: 'decimal', step: 'any' }}
          value={sample.lowerdepth ?? ''}
          onChange={(e) => handleVeldmonsterChange(sample.id, 'lowerdepth', e.target.value ? parseFloat(e.target.value) : null)}
          fullWidth
          margin="normal"
          sx={{ flex: 1 }}
          disabled={isReadOnly}
        />
        <Box sx={{ flex: 1 }}>
          <AutocompleteField
            label="Eenheid"
            value={sample.lowerdepth_uom}
            onChange={(newValue) => handleVeldmonsterChange(sample.id, 'lowerdepth_uom', newValue)}
            options={lengthUnitsData?.['Eenheid'] || []}
            loading={lengthUnitsLoading}
            keyPrefix={`lowerdepth-uom-${sample.id}`}
            isReadOnly={isReadOnly}
          />
        </Box>
      </Box>
      <AutocompleteField
        label="Onderdiepte conditie"
        value={sample.lowerdepth_condition}
        onChange={(newValue) => handleVeldmonsterChange(sample.id, 'lowerdepth_condition', newValue)}
        options={referentieVlakData?.['Hoedanigheid'] || []}
        loading={referentieVlakLoading}
        keyPrefix={`lowerdepth-cond-${sample.id}`}
        isReadOnly={isReadOnly}
      />

      {/* Boring (verplicht) */}
      <Autocomplete
        fullWidth
        options={boreholes}
        getOptionLabel={(option) => option.name || 'Naamloos'}
        value={boreholes.find(b => b.id === sample.borehole_id) || null}
        onChange={(_, newValue) => {
          handleVeldmonsterChange(sample.id, 'borehole_id', newValue?.id);
        }}
        disabled={isReadOnly}
        renderInput={(params) => (
          <TextField {...params} label="Boring" margin="normal" />
        )}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        renderOption={(props, option) => (
          <li {...props} key={`${sample.id}-borehole-${option.id}`}>
            {option.name}
          </li>
        )}
      />

      {/* Gekoppeld aan analysemonster */}
      <Autocomplete
        fullWidth
        options={analysemonsters}
        getOptionLabel={(option) => option.name || 'Naamloos'}
        value={analysemonsters.find(s => s.id === sample.sample_id) || null}
        onChange={(_, newValue) => {
          handleVeldmonsterChange(sample.id, 'sample_id', newValue?.id);
        }}
        disabled={isReadOnly}
        renderInput={(params) => (
          <TextField {...params} label="Gekoppeld analysemonster" margin="normal" />
        )}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        renderOption={(props, option) => (
          <li {...props} key={`${sample.id}-sample-${option.id}`}>
            {option.name}
          </li>
        )}
      />

    </Box>
  );
});
// --- End VeldmonsterForm Component ---

export default function SampleTab({ document, isReadOnly }) {
  const [expandedItem, setExpandedItem] = useState(null);
  const [error, setError] = useState(null);
  const [boreholes, setBoreholes] = useState([]);
  const [groupedByBorehole, setGroupedByBorehole] = useState({});
  const [isCreating, setIsCreating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedSampleId, setSelectedSampleId] = useState(null);
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isBulkCreateDialogOpen, setIsBulkCreateDialogOpen] = useState(false);
  const [isBulkCreating, setIsBulkCreating] = useState(false);
  const [bulkCreateData, setBulkCreateData] = useState({
    namePrefix: '',
    starttime: null,
    materialclass: null,
    borehole_id: null,
    numSamples: 2,
    depthUnit: null,
    depthCondition: null,
    sampleDepths: [{ start: null }, { start: null, end: null }]
  });
  const [newVeldmonster, setNewVeldmonster] = useState({
    name: '',
    document_id: document.id,
    starttime: null,
    materialclass: null,
    upperdepth: null,
    upperdepth_uom: null,
    upperdepth_condition: null,
    lowerdepth: null,
    lowerdepth_uom: null,
    lowerdepth_condition: null,
    borehole_id: null,
    sample_id: null
  });
  const [selectedBoreholeId, setSelectedBoreholeId] = useState(null);
  const [isBoreholeDeleteDialogOpen, setIsBoreholeDeleteDialogOpen] = useState(false);
  const [isDeletingBorehole, setIsDeletingBorehole] = useState(false);

  const veldmonsterParams = useMemo(() => ({
    document_id: document.id,
    type: 'veld'
  }), [document.id]);

  const analysemonsterParams = useMemo(() => ({
    document_id: document.id,
    type: 'analyse'
  }), [document.id]);

  const {
    samples: fetchedVeldmonsters,
    loading: veldmonstersLoading,
    error: veldmonstersError,
    refetch: refetchVeldmonsters
  } = useSamples(veldmonsterParams);

  const {
    samples: fetchedAnalysemonsters,
    loading: analysemonstersLoading,
    error: analysemonstersError
  } = useSamples(analysemonsterParams);

  const [editableVeldmonsters, setEditableVeldmonsters] = useState([]);

  const technicalNames = ['Eenheid', 'Hoedanigheid', 'BodemlaagType', 'Compartiment'];
  const { domainValues, loading: domainValuesLoading } = useDomainValues(technicalNames);

  const {
    domainValues: lengthUnitsData,
    loading: lengthUnitsLoading,
  } = useDomainValues({ technical_name: 'Eenheid', unit_dimension: 'l' });

  const {
    domainValues: referentieVlakData,
    loading: referentieVlakLoading,
  } = useDomainValues({ technical_name: 'Hoedanigheid', group_name: 'ReferentieVlak', sortByFrequency: true });

  // Find the domain_value_id for 'meter'
  const meterUnitId = useMemo(() => {
    const units = lengthUnitsData?.['Eenheid'];
    if (!units) return null;
    const meterOption = units.find(unit => unit.description?.toLowerCase() === 'meter');
    return meterOption?.domain_value_id || null;
  }, [lengthUnitsData]);

  // Find the domain_value_id for 't.o.v. maaiveld'
  const defaultConditionId = useMemo(() => {
    const conditions = referentieVlakData?.['Hoedanigheid'];
    if (!conditions) return null;
    return conditions.find(cond => cond.description?.toLowerCase() === 't.o.v. maaiveld')?.domain_value_id || null;
  }, [referentieVlakData]);

  // Effect to invalidate cache on unmount
  useEffect(() => {
    return () => {
      if (document?.id) {
        invalidateSampleCache(document.id);
      }
    };
  }, [document?.id]);

  // Restore fetchBoreholes effect
  useEffect(() => {
    const fetchBoreholes = async () => {
      try {
        const { data: projectData } = await supabase
          .from('document_project')
          .select('id')
          .eq('document_id', document.id)
          .single();

        if (projectData) {
          const { data } = await supabase
            .from('document_borehole')
            .select('id, name')
            .eq('project_id', projectData.id);
          
          if (data) {
            setBoreholes(data);
          }
        }
      } catch (error) {
        console.error('Error fetching boreholes:', error);
      }
    };

    if (document?.id) {
      fetchBoreholes();
    }
  }, [document?.id]);

  // Update useEffect to call refetchVeldmonsters when meterUnitId is ready
  useEffect(() => {
    // Only refetch if document exists, units are loaded, and meter ID is resolved.
    if (document?.id && !lengthUnitsLoading && meterUnitId !== undefined) {
      console.log("Refetching veldmonsters due to meterUnitId availability...");
      refetchVeldmonsters();
    }
  }, [document?.id, lengthUnitsLoading, meterUnitId, refetchVeldmonsters]);

  // Rename and update function to set default units and conditions
  const setDefaultValues = useCallback(async (samples) => {
    if (!meterUnitId && !defaultConditionId) return samples; // Check if either ID is missing

    const updatePromises = [];
    const updatedSamples = samples.map(sample => {
      let updates = {};
      let needsUpdate = false;
      let updatedSample = { ...sample };

      // Default Units
      if (meterUnitId) {
        if (sample.upperdepth_uom == null) {
          updates.upperdepth_uom = meterUnitId;
          updatedSample.upperdepth_uom = meterUnitId;
          needsUpdate = true;
        }
        if (sample.lowerdepth_uom == null) {
          updates.lowerdepth_uom = meterUnitId;
          updatedSample.lowerdepth_uom = meterUnitId;
          needsUpdate = true;
        }
      }

      // Default Conditions
      if (defaultConditionId) {
        if (sample.upperdepth_condition == null) {
            updates.upperdepth_condition = defaultConditionId;
            updatedSample.upperdepth_condition = defaultConditionId;
            needsUpdate = true;
        }
        if (sample.lowerdepth_condition == null) {
            updates.lowerdepth_condition = defaultConditionId;
            updatedSample.lowerdepth_condition = defaultConditionId;
            needsUpdate = true;
        }
      }

      if (needsUpdate) {
        updatePromises.push(
          supabase
            .from('document_sample')
            .update(updates)
            .eq('id', sample.id)
        );
      }
      return updatedSample;
    });

    if (updatePromises.length > 0) {
      console.log(`Setting default 'meter' unit for ${updatePromises.length} veldmonster fields...`);
      const results = await Promise.allSettled(updatePromises);
      const updateErrors = results.filter(r => r.status === 'rejected').length;
      if (updateErrors > 0) {
        setError(prev => {
          const errorMsg = `Kon voor ${updateErrors} veld(en) de standaard eenheid ('meter') niet opslaan.`;
          return prev ? `${prev}\n${errorMsg}` : errorMsg;
        });
      }
    }

    return updatedSamples;
  }, [meterUnitId, defaultConditionId]);

  // Update the effect that processes fetched veldmonsters
  useEffect(() => {
    if (fetchedVeldmonsters) {
      const processVeldmonsters = async () => {
        const mappedSamples = fetchedVeldmonsters.map(s => ({
          ...s,
          materialclass: s.materialclass?.domain_value_id ?? s.materialclass ?? null,
          upperdepth_uom: s.upperdepth_uom?.domain_value_id ?? s.upperdepth_uom ?? null,
          upperdepth_condition: s.upperdepth_condition?.domain_value_id ?? s.upperdepth_condition ?? null,
          lowerdepth_uom: s.lowerdepth_uom?.domain_value_id ?? s.lowerdepth_uom ?? null,
          lowerdepth_condition: s.lowerdepth_condition?.domain_value_id ?? s.lowerdepth_condition ?? null,
          borehole_id: s.borehole_id?.id ?? s.borehole_id ?? null,
          sample_id: s.sample_id?.id ?? s.sample_id ?? null,
        }));

        // Apply default units and conditions
        const samplesWithDefaults = await setDefaultValues(mappedSamples);

        if (JSON.stringify(samplesWithDefaults) !== JSON.stringify(editableVeldmonsters)) {
          setEditableVeldmonsters(samplesWithDefaults || []);
        }
      };

      processVeldmonsters();
    }
  }, [fetchedVeldmonsters, setDefaultValues, meterUnitId, defaultConditionId]);

  useEffect(() => {
    return () => {
      if (document?.id) {
        invalidateSampleCache(document.id);
      }
    };
  }, [document?.id]);

  useEffect(() => {
    if (fetchedVeldmonsters) {
      const grouped = {};
      editableVeldmonsters.forEach(veld => {
        const boreholeId = veld.borehole_id || 'no_borehole';
        if (!grouped[boreholeId]) {
          grouped[boreholeId] = [];
        }
        grouped[boreholeId].push(veld);
      });

      // 1. Sort samples within each group by name
      for (const boreholeId in grouped) {
        grouped[boreholeId].sort((a, b) => {
          const nameA = a.name || '';
          const nameB = b.name || '';
          return nameA.localeCompare(nameB, 'nl', { numeric: true });
        });
      }

      // 2. Sort the groups (boreholes) by name, keeping 'no_borehole' at the end
      const sortedGroupedEntries = Object.entries(grouped).sort(([idA], [idB]) => {
        if (idA === 'no_borehole') return 1;
        if (idB === 'no_borehole') return -1;
        const boreholeA = boreholes.find(b => b.id === idA);
        const boreholeB = boreholes.find(b => b.id === idB);
        const nameA = boreholeA?.name || '';
        const nameB = boreholeB?.name || '';
        return nameA.localeCompare(nameB, 'nl', { numeric: true });
      });

      // 3. Reconstruct the sorted object
      const sortedGrouped = Object.fromEntries(sortedGroupedEntries);
      setGroupedByBorehole(sortedGrouped);

    } else if (!veldmonstersLoading && editableVeldmonsters.length === 0) {
      setGroupedByBorehole({});
    }
  }, [editableVeldmonsters, veldmonstersLoading, boreholes]);

  const debouncedSave = useCallback(
    debounce(async (id, updates) => {
      if (isReadOnly) {
        console.log("Read-only mode: Save prevented for veldmonster", id);
        return;
      }
      try {
        const { error } = await supabase
          .from('document_sample')
          .update(updates)
          .eq('id', id);

        if (error) throw error;

        setError(null);

        invalidateSampleCache(document.id);

      } catch (error) {
        console.error('Save error:', error);
        setError(`Fout bij opslaan veldmonster ${id.substring(0,4)}...: ${error.message}`);
      }
    }, 1000),
    [document.id, isReadOnly]
  );

  const handleVeldmonsterChange = useCallback((id, field, value) => {
    if (isReadOnly) {
      console.log("Read-only mode: Change prevented for veldmonster", id, field);
      return;
    }
    setEditableVeldmonsters(prevSamples =>
      prevSamples.map(s =>
        s.id === id ? { ...s, [field]: value } : s
      )
    );
    debouncedSave(id, { [field]: value });
  }, [debouncedSave, isReadOnly]);

  const handleOpenCreateDialog = (boreholeId = null) => {
    setNewVeldmonster({
      name: '',
      document_id: document.id,
      starttime: null,
      materialclass: null,
      upperdepth: null,
      upperdepth_uom: null,
      upperdepth_condition: null,
      lowerdepth: null,
      lowerdepth_uom: null,
      lowerdepth_condition: null,
      borehole_id: boreholeId && boreholeId !== 'no_borehole' ? boreholeId : null,
      sample_id: null,
      type: 'veld'
    });
    setIsCreating(true);
  };

  const handleCreateVeldmonster = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      if (!newVeldmonster.name) {
         throw new Error("Naam is verplicht.");
      }

      const createData = {
        id: uuidv4(),
        document_id: document.id,
        name: newVeldmonster.name,
        starttime: newVeldmonster.starttime,
        materialclass: newVeldmonster.materialclass,
        upperdepth: newVeldmonster.upperdepth,
        upperdepth_uom: newVeldmonster.upperdepth_uom || meterUnitId, // Use meterUnitId as default
        upperdepth_condition: newVeldmonster.upperdepth_condition || defaultConditionId, // Use defaultConditionId as default
        lowerdepth: newVeldmonster.lowerdepth,
        lowerdepth_uom: newVeldmonster.lowerdepth_uom || meterUnitId, // Use meterUnitId as default
        lowerdepth_condition: newVeldmonster.lowerdepth_condition || defaultConditionId, // Use defaultConditionId as default
        borehole_id: newVeldmonster.borehole_id,
        sample_id: newVeldmonster.sample_id,
        type: 'veld'
      };

      const { data, error } = await supabase
        .from('document_sample')
        .insert([createData])
        .select()
        .single();

      if (error) throw error;

      setEditableVeldmonsters(prev => [...prev, data]);

      setIsCreating(false);
      setNewVeldmonster({
        name: '', document_id: document.id, starttime: null, materialclass: null,
        upperdepth: null, upperdepth_uom: null, upperdepth_condition: null,
        lowerdepth: null, lowerdepth_uom: null, lowerdepth_condition: null,
        borehole_id: null, sample_id: null
      });
      invalidateSampleCache(document.id);
      refetchVeldmonsters();

    } catch (error) {
      console.error('Error creating veldmonster:', error);
      setError(`Fout bij aanmaken veldmonster: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDuplicateDialog = (originalSampleId) => {
    setError(null);

    // 1. Vind het originele monster
    const originalSample = editableVeldmonsters.find(s => s.id === originalSampleId);
    if (!originalSample) {
      setError("Originele veldmonster niet gevonden voor duplicatie.");
      return;
    }

    // 2. Bereid data voor met aangepaste naam (zonder nieuwe ID!)
    const duplicateInitialData = {
      ...originalSample,
      name: `${originalSample.name || 'Naamloos'} (kopie)`, // Pas naam aan
    };

    // Verwijder velden die niet direct gekopieerd moeten worden voor de *initial state*
    delete duplicateInitialData.id; // ID wordt later gegenereerd bij aanmaken
    delete duplicateInitialData.__typename;
    delete duplicateInitialData.borehole; 
    delete duplicateInitialData.sample; 
    
    // Zorg dat gerelateerde IDs correct zijn (indien ze objecten waren)
    duplicateInitialData.borehole_id = originalSample.borehole_id;
    duplicateInitialData.sample_id = originalSample.sample_id;
    duplicateInitialData.materialclass = originalSample.materialclass;
    duplicateInitialData.upperdepth_uom = originalSample.upperdepth_uom;
    duplicateInitialData.upperdepth_condition = originalSample.upperdepth_condition;
    duplicateInitialData.lowerdepth_uom = originalSample.lowerdepth_uom;
    duplicateInitialData.lowerdepth_condition = originalSample.lowerdepth_condition;
    duplicateInitialData.type = 'veld'; // Zorg dat het type correct is

    // 3. Zet de voorbereide data in de state voor de dialoog
    setNewVeldmonster(duplicateInitialData);

    // 4. Open de "Nieuw" dialoog
    setIsCreating(true);
  };

  const handleDeleteVeldmonster = async (sampleId) => {
    try {
      setIsDeleting(true);
      setError(null);
      await deleteSample(sampleId);

      setEditableVeldmonsters(prev => prev.filter(s => s.id !== sampleId));

      setIsDeleteDialogOpen(false);
      setSelectedSampleId(null);
      invalidateSampleCache(document.id);
      refetchVeldmonsters();

    } catch (error) {
      console.error('Error deleting veldmonster:', error);
      setError(`Fout bij verwijderen veldmonster: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDeleteVeldmonsters = async () => {
    try {
      setIsBulkDeleting(true);
      setError(null);

      // Get all sample IDs
      const sampleIds = editableVeldmonsters.map(sample => sample.id);

      // Delete all samples in parallel
      await Promise.all(sampleIds.map(id => deleteSample(id)));

      // Clear local state
      setEditableVeldmonsters([]);
      setIsBulkDeleteDialogOpen(false);
      
      // Refresh data
      invalidateSampleCache(document.id);
      refetchVeldmonsters();

    } catch (error) {
      console.error('Error deleting all veldmonsters:', error);
      setError(`Fout bij verwijderen van alle veldmonsters: ${error.message}`);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleNumSamplesChange = (value) => {
    const numSamples = value === '' ? '' : parseInt(value);
    
    setBulkCreateData(prev => {
      let newSampleDepths;
      
      if (!numSamples || numSamples < 2) {
        newSampleDepths = [];
      } else {
        // Create array of sample depths, last one needs end depth
        newSampleDepths = Array(numSamples).fill(null).map((_, index) => {
          if (index === numSamples - 1) {
            return { start: null, end: null };
          }
          return { start: null };
        });
      }

      return {
        ...prev,
        numSamples,
        sampleDepths: newSampleDepths
      };
    });
  };

  const handleSampleDepthChange = (index, field, value) => {
    setBulkCreateData(prev => {
      const newSampleDepths = [...prev.sampleDepths];
      const numValue = value === '' ? null : parseFloat(value);
      
      if (field === 'start') {
        newSampleDepths[index] = { ...newSampleDepths[index], start: numValue };
        
        // If this isn't the first sample, set this start depth as the end depth of the previous sample
        if (index > 0) {
          newSampleDepths[index - 1] = { ...newSampleDepths[index - 1], end: numValue };
        }
      } else if (field === 'end' && index === newSampleDepths.length - 1) {
        // Only allow setting end depth for the last sample
        newSampleDepths[index] = { ...newSampleDepths[index], end: numValue };
      }

      return {
        ...prev,
        sampleDepths: newSampleDepths
      };
    });
  };

  const handleBulkCreateVeldmonsters = async () => {
    try {
      // Validate required fields
      if (!bulkCreateData.namePrefix || 
          !bulkCreateData.materialclass || !bulkCreateData.borehole_id || 
          !bulkCreateData.depthUnit || !bulkCreateData.sampleDepths.length) {
        throw new Error("Vul alle verplichte velden in");
      }

      // Validate depths
      const invalidDepths = bulkCreateData.sampleDepths.some((sample, index) => {
        // All samples need a start depth
        if (sample.start === null) return true;
        
        // Last sample needs both start and end depth
        if (index === bulkCreateData.sampleDepths.length - 1) {
          if (sample.end === null) return true;
          // End depth should be greater than start depth for last sample
          if (sample.end <= sample.start) return true;
        }
        
        // Each sample's start depth should be greater than previous sample's start depth
        if (index > 0) {
          const prevSample = bulkCreateData.sampleDepths[index - 1];
          if (sample.start <= prevSample.start) return true;
        }
        
        return false;
      });

      if (invalidDepths) {
        throw new Error("Controleer de dieptes. Zorg dat ze in oplopende volgorde zijn en dat de laatste monster een einddiepte heeft die groter is dan de startdiepte.");
      }

      setIsBulkCreating(true);
      setError(null);

      const samples = bulkCreateData.sampleDepths.map((sample, index) => {
        return {
          id: uuidv4(),
          document_id: document.id,
          name: `${bulkCreateData.namePrefix}-${index + 1}`,
          starttime: bulkCreateData.starttime,
          materialclass: bulkCreateData.materialclass,
          upperdepth: sample.start,
          upperdepth_uom: bulkCreateData.depthUnit,
          upperdepth_condition: bulkCreateData.depthCondition,
          lowerdepth: index === bulkCreateData.sampleDepths.length - 1 ? sample.end : bulkCreateData.sampleDepths[index + 1]?.start,
          lowerdepth_uom: bulkCreateData.depthUnit,
          lowerdepth_condition: bulkCreateData.depthCondition,
          borehole_id: bulkCreateData.borehole_id,
          type: 'veld'
        };
      });

      const { error } = await supabase
        .from('document_sample')
        .insert(samples);

      if (error) throw error;

      setIsBulkCreateDialogOpen(false);
      setBulkCreateData({
        namePrefix: '',
        starttime: null,
        materialclass: null,
        borehole_id: null,
        numSamples: 2,
        depthUnit: null,
        depthCondition: null,
        sampleDepths: [{ start: null }, { start: null, end: null }]
      });

      invalidateSampleCache(document.id);
      refetchVeldmonsters();

    } catch (error) {
      console.error('Error creating bulk veldmonsters:', error);
      setError(`Fout bij aanmaken veldmonsters: ${error.message}`);
    } finally {
      setIsBulkCreating(false);
    }
  };

  const handleDeleteBoreholeVeldmonsters = async (boreholeId) => {
    try {
      setIsDeletingBorehole(true);
      setError(null);

      // Get all sample IDs for this borehole
      const sampleIds = editableVeldmonsters
        .filter(sample => sample.borehole_id === boreholeId)
        .map(sample => sample.id);

      // Delete all samples in parallel
      await Promise.all(sampleIds.map(id => deleteSample(id)));

      // Clear local state
      setEditableVeldmonsters(prev => prev.filter(sample => sample.borehole_id !== boreholeId));
      setIsBoreholeDeleteDialogOpen(false);
      setSelectedBoreholeId(null);
      
      // Refresh data
      invalidateSampleCache(document.id);
      refetchVeldmonsters();

    } catch (error) {
      console.error('Error deleting borehole veldmonsters:', error);
      setError(`Fout bij verwijderen van veldmonsters: ${error.message}`);
    } finally {
      setIsDeletingBorehole(false);
    }
  };

  const combinedLoading = veldmonstersLoading || analysemonstersLoading || domainValuesLoading || lengthUnitsLoading || referentieVlakLoading;
  const combinedError = veldmonstersError || analysemonstersError || error;

  if (combinedLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const hasVeldmonsters = editableVeldmonsters.length > 0;

  const getSampleFromEditable = (id) => editableVeldmonsters.find(s => s.id === id);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Veldmonsters</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setIsBulkCreateDialogOpen(true)}
            disabled={isReadOnly}
          >
            Bulk Toevoegen
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setIsBulkDeleteDialogOpen(true)}
            disabled={!hasVeldmonsters || isReadOnly}
          >
            Verwijder Alles
          </Button>
        </Box>
      </Box>

      {combinedError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {combinedError}
        </Alert>
      )}

      {/* Add Bulk Delete Dialog */}
      <Dialog 
        open={isBulkDeleteDialogOpen} 
        onClose={() => setIsBulkDeleteDialogOpen(false)}
      >
        <DialogTitle>Alle Veldmonsters Verwijderen</DialogTitle>
        <DialogContent>
          <Typography>
            Weet u zeker dat u ALLE veldmonsters wilt verwijderen? 
            Dit kan niet ongedaan worden gemaakt.
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            Let op: Er worden {editableVeldmonsters.length} veldmonsters verwijderd.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsBulkDeleteDialogOpen(false)} disabled={isReadOnly}>
            Annuleren
          </Button>
          <Button 
            onClick={handleBulkDeleteVeldmonsters}
            color="error"
            disabled={isBulkDeleting || isReadOnly}
            startIcon={isBulkDeleting ? <CircularProgress size={24} /> : null}
          >
            {isBulkDeleting ? 'Bezig met verwijderen...' : 'Alles Verwijderen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Bulk Create Dialog */}
      <Dialog
        open={isBulkCreateDialogOpen}
        onClose={() => setIsBulkCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperComponent={BulkDraggableDialog}
        aria-labelledby="bulk-create-dialog-title"
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'transparent',
            },
          },
        }}
        sx={{
          '& .MuiDialog-paper': {
            position: 'fixed',
            left: 'auto',
            right: '20%',
            top: '10%',
            margin: 0,
            maxHeight: '80vh',
            overflowY: 'auto',
            width: '400px'
          }
        }}
      >
        <DialogTitle 
          id="bulk-create-dialog-title" 
          style={{ cursor: 'move', display: 'flex', alignItems: 'center' }}
        >
          <DragIndicatorIcon sx={{ mr: 1 }} />
          Meerdere Veldmonsters Toevoegen
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <TextField
            label="Naam prefix"
            value={bulkCreateData.namePrefix}
            onChange={(e) => setBulkCreateData(prev => ({ ...prev, namePrefix: e.target.value }))}
            fullWidth
            margin="normal"
            required
            disabled={isReadOnly}
          />

          <DatePicker
            label="Startdatum"
            value={bulkCreateData.starttime ? new Date(bulkCreateData.starttime) : null}
            onChange={(newValue) => {
              setBulkCreateData(prev => ({ ...prev, starttime: newValue?.toISOString() }));
            }}
            format="dd-MM-yyyy"
            disabled={isReadOnly}
            slotProps={{
              textField: {
                fullWidth: true,
                margin: "normal",
                required: true,
                InputProps: {
                  endAdornment: (
                    <>
                      {bulkCreateData.starttime && (
                        <InputAdornment position="end" sx={{ marginRight: -1.5 }}>
                          <IconButton
                            aria-label="clear date"
                            onClick={(e) => {
                              e.stopPropagation();
                              setBulkCreateData(prev => ({ ...prev, starttime: null }));
                            }}
                            edge="end"
                            size="small"
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      )}
                    </>
                  ),
                }
              }
            }}
          />

          <AutocompleteField
            label="Veld matrix"
            value={bulkCreateData.materialclass}
            onChange={(newValue) => setBulkCreateData(prev => ({ ...prev, materialclass: newValue }))}
            options={domainValues['Compartiment']}
            loading={domainValuesLoading}
            keyPrefix="bulk-matrix"
            required
            isReadOnly={isReadOnly}
          />

          <AutocompleteField
            label="Boring (verplicht)"
            value={bulkCreateData.borehole_id}
            onChange={(newValue) => setBulkCreateData(prev => ({ ...prev, borehole_id: newValue }))}
            options={boreholes}
            loading={false}
            keyPrefix="bulk-borehole"
            isReadOnly={isReadOnly}
          />

          <TextField
            label="Aantal monsters"
            type="number"
            value={bulkCreateData.numSamples ?? ''}
            onChange={(e) => handleNumSamplesChange(e.target.value)}
            fullWidth
            margin="normal"
            required
            inputProps={{ min: 2 }}
            disabled={isReadOnly}
          />

          <AutocompleteField
            label="Diepte eenheid"
            value={bulkCreateData.depthUnit}
            onChange={(newValue) => setBulkCreateData(prev => ({ ...prev, depthUnit: newValue }))}
            options={lengthUnitsData?.['Eenheid'] || []}
            loading={lengthUnitsLoading}
            keyPrefix="bulk-depth-unit"
            required
            isReadOnly={isReadOnly}
          />

          <AutocompleteField
            label="Diepte conditie"
            value={bulkCreateData.depthCondition}
            onChange={(newValue) => setBulkCreateData(prev => ({ ...prev, depthCondition: newValue }))}
            options={referentieVlakData?.['Hoedanigheid'] || []}
            loading={referentieVlakLoading}
            keyPrefix="bulk-depth-condition"
            isReadOnly={isReadOnly}
          />

          {bulkCreateData.sampleDepths.map((sample, index) => (
            <Box key={index} sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                Monster {index + 1}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  label="Start diepte"
                  type="number"
                  value={sample.start ?? ''}
                  onChange={(e) => handleSampleDepthChange(index, 'start', e.target.value)}
                  fullWidth
                  margin="normal"
                  required
                  inputProps={{ step: 'any' }}
                  disabled={isReadOnly}
                />
                {index === bulkCreateData.sampleDepths.length - 1 && (
                  <TextField
                    label="Eind diepte"
                    type="number"
                    value={sample.end ?? ''}
                    onChange={(e) => handleSampleDepthChange(index, 'end', e.target.value)}
                    fullWidth
                    margin="normal"
                    required
                    inputProps={{ step: 'any' }}
                    disabled={isReadOnly}
                  />
                )}
              </Box>
              {index > 0 && (
                <Typography variant="caption" color="textSecondary">
                  Eind diepte van vorig monster: {sample.start ?? '-'}
                </Typography>
              )}
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsBulkCreateDialogOpen(false)}>
            Annuleren
          </Button>
          <Button
            onClick={handleBulkCreateVeldmonsters}
            variant="contained"
            disabled={isBulkCreating || !bulkCreateData.namePrefix || !bulkCreateData.borehole_id || !bulkCreateData.depthUnit || isReadOnly}
            startIcon={isBulkCreating ? <CircularProgress size={24} /> : null}
          >
            {isBulkCreating ? 'Bezig met aanmaken...' : 'Aanmaken'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Borehole Delete Dialog */}
      <Dialog 
        open={isBoreholeDeleteDialogOpen} 
        onClose={() => setIsBoreholeDeleteDialogOpen(false)}
      >
        <DialogTitle>Veldmonsters van Boring Verwijderen</DialogTitle>
        <DialogContent>
          <Typography>
            Weet u zeker dat u alle veldmonsters van deze boring wilt verwijderen? 
            Dit kan niet ongedaan worden gemaakt.
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            Let op: Er worden {editableVeldmonsters.filter(s => s.borehole_id === selectedBoreholeId).length} veldmonsters verwijderd.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsBoreholeDeleteDialogOpen(false)} disabled={isReadOnly}>
            Annuleren
          </Button>
          <Button 
            onClick={() => handleDeleteBoreholeVeldmonsters(selectedBoreholeId)}
            color="error"
            disabled={isDeletingBorehole || isReadOnly}
            startIcon={isDeletingBorehole ? <CircularProgress size={24} /> : null}
          >
            {isDeletingBorehole ? 'Bezig met verwijderen...' : 'Verwijderen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Single Delete Dialog */}
      <Dialog 
        open={isDeleteDialogOpen} 
        onClose={() => setIsDeleteDialogOpen(false)}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'transparent',
            },
          },
        }}
      >
        <DialogTitle>Veldmonster verwijderen</DialogTitle>
        <DialogContent>
          <Typography>
            Weet u zeker dat u dit veldmonster wilt verwijderen? 
            Dit kan niet ongedaan worden gemaakt.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>
            Annuleren
          </Button>
          <Button 
            onClick={() => handleDeleteVeldmonster(selectedSampleId)}
            color="error"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={24} /> : null}
          >
            {isDeleting ? 'Bezig met verwijderen...' : 'Verwijderen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Single Dialog */}
      <Dialog
        open={isCreating}
        onClose={() => setIsCreating(false)}
        PaperComponent={DraggableDialog}
        aria-labelledby="draggable-dialog-title"
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'transparent',
            },
          },
        }}
        sx={{
          '& .MuiDialog-paper': {
            position: 'fixed',
            left: 'auto',
            right: '20%',
            top: '10%',
            margin: 0,
          }
        }}
      >
        <DialogTitle 
          id="draggable-dialog-title" 
          style={{ cursor: 'move', display: 'flex', alignItems: 'center' }}
        >
          <DragIndicatorIcon sx={{ mr: 1 }} />
          Nieuw Veldmonster
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Naam"
            value={newVeldmonster.name}
            onChange={(e) => setNewVeldmonster(prev => ({ ...prev, name: e.target.value }))}
            fullWidth
            margin="normal"
            autoFocus
          />
          
          <DatePicker
            label="Startdatum"
            value={newVeldmonster.starttime ? new Date(newVeldmonster.starttime) : null}
            onChange={(newValue) => {
              const validDate = newValue instanceof Date && !isNaN(newValue);
              setNewVeldmonster(prev => ({
                ...prev,
                starttime: validDate ? newValue.toISOString() : null
              }));
            }}
            format="dd-MM-yyyy"
            slotProps={{
              textField: {
                fullWidth: true,
                margin: "normal",
                InputProps: {
                  endAdornment: (
                    <>
                      {newVeldmonster.starttime && (
                        <InputAdornment position="end" sx={{ marginRight: -1.5 }}>
                          <IconButton
                            aria-label="clear date"
                            onClick={(e) => {
                              e.stopPropagation();
                              setNewVeldmonster(prev => ({ ...prev, starttime: null }));
                            }}
                            edge="end"
                            size="small"
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      )}
                    </>
                  ),
                }
              }
            }}
          />

          <AutocompleteField
            label="Veld matrix"
            value={newVeldmonster.materialclass}
            onChange={(newValue) => setNewVeldmonster(prev => ({ ...prev, materialclass: newValue }))}
            options={domainValues['Compartiment']}
            loading={domainValuesLoading}
            keyPrefix="new-matrix"
          />
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
            <TextField
              label="Bovendiepte"
              type="number"
              inputProps={{ inputMode: 'decimal', step: 'any' }}
              value={newVeldmonster.upperdepth ?? ''}
              onChange={(e) => setNewVeldmonster(prev => ({ ...prev, upperdepth: e.target.value ? parseFloat(e.target.value) : null }))}
              fullWidth
              margin="normal"
              sx={{ flex: 1 }}
            />
            <Box sx={{ flex: 1 }}>
              <AutocompleteField
                label="Eenheid"
                value={newVeldmonster.upperdepth_uom}
                onChange={(newValue) => setNewVeldmonster(prev => ({ ...prev, upperdepth_uom: newValue }))}
                options={lengthUnitsData?.['Eenheid'] || []}
                loading={lengthUnitsLoading}
                keyPrefix="new-upperdepth-uom"
              />
            </Box>
          </Box>

          <AutocompleteField
            label="Bovendiepte Conditie"
            value={newVeldmonster.upperdepth_condition}
            onChange={(newValue) => setNewVeldmonster(prev => ({ ...prev, upperdepth_condition: newValue }))}
            options={referentieVlakData?.['Hoedanigheid'] || []}
            loading={referentieVlakLoading}
            keyPrefix="new-upperdepth-cond"
          />

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
            <TextField
              label="Onderdiepte"
              type="number"
              inputProps={{ inputMode: 'decimal', step: 'any' }}
              value={newVeldmonster.lowerdepth ?? ''}
              onChange={(e) => setNewVeldmonster(prev => ({ ...prev, lowerdepth: e.target.value ? parseFloat(e.target.value) : null }))}
              fullWidth
              margin="normal"
              sx={{ flex: 1 }}
            />
            <Box sx={{ flex: 1 }}>
              <AutocompleteField
                label="Eenheid"
                value={newVeldmonster.lowerdepth_uom}
                onChange={(newValue) => setNewVeldmonster(prev => ({ ...prev, lowerdepth_uom: newValue }))}
                options={lengthUnitsData?.['Eenheid'] || []}
                loading={lengthUnitsLoading}
                keyPrefix="new-lowerdepth-uom"
              />
            </Box>
          </Box>

          <AutocompleteField
            label="Onderdiepte conditie"
            value={newVeldmonster.lowerdepth_condition}
            onChange={(newValue) => setNewVeldmonster(prev => ({ ...prev, lowerdepth_condition: newValue }))}
            options={referentieVlakData?.['Hoedanigheid'] || []}
            loading={referentieVlakLoading}
            keyPrefix="new-lowerdepth-cond"
          />

          <Autocomplete
            fullWidth
            options={boreholes}
            getOptionLabel={(option) => option.name || 'Naamloos'}
            value={boreholes.find(b => b.id === newVeldmonster.borehole_id) || null}
            onChange={(_, newValue) => {
              setNewVeldmonster(prev => ({ ...prev, borehole_id: newValue?.id }));
            }}
            renderInput={(params) => (
              <TextField {...params} label="Boring (verplicht)" margin="normal" required />
            )}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderOption={(props, option) => (
              <li {...props} key={`new-borehole-${option.id}`}>
                {option.name}
              </li>
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCreating(false)}>
            Annuleren
          </Button>
          <Button 
            onClick={handleCreateVeldmonster}
            variant="contained" 
            disabled={isSubmitting || !newVeldmonster.borehole_id}
            startIcon={isSubmitting ? <CircularProgress size={24} /> : null}
          >
            {isSubmitting ? 'Bezig met aanmaken...' : 'Aanmaken'}
          </Button>
        </DialogActions>
      </Dialog>

      {!hasVeldmonsters && !veldmonstersLoading ? (
        <Box sx={{ p: 2 }}>
          <Typography variant="body1">Geen veldmonsters gevonden</Typography>
        </Box>
      ) : (
        <List>
          {Object.entries(groupedByBorehole).map(([boreholeId, groupVeldmonsters]) => {
            const borehole = boreholes.find(b => b.id === boreholeId);
            const groupTitle = boreholeId === 'no_borehole'
              ? 'Geen Boring Gekoppeld'
              : borehole?.name || `Boring ID: ${boreholeId}`;

            return (
              <Box key={boreholeId} sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    bgcolor: 'primary.light',
                    color: 'primary.contrastText',
                    p: 1,
                    borderRadius: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  {groupTitle}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title={`Nieuw veldmonster koppelen aan ${groupTitle}`}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenCreateDialog(boreholeId)}
                          disabled={boreholeId === 'no_borehole' || isReadOnly}
                          sx={{ color: 'primary.contrastText' }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {boreholeId !== 'no_borehole' && groupVeldmonsters.length > 0 && (
                      <Tooltip title={`Verwijder alle veldmonsters van ${groupTitle}`}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedBoreholeId(boreholeId);
                              setIsBoreholeDeleteDialogOpen(true);
                            }}
                            sx={{ color: 'primary.contrastText' }}
                            disabled={isReadOnly}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                </Typography>

                {groupVeldmonsters.map((sample) => {
                  const linkedAnalysemonster = (fetchedAnalysemonsters || []).find(m => m.id === sample.sample_id);
                  const displayName = `${sample.name || 'Naamloos Veldmonster'}`;

                  return (
                    <React.Fragment key={sample.id}>
                      <ListItem
                        divider
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between'
                        }}
                      >
                        <ListItemText 
                          primary={displayName}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title="Dupliceer veldmonster">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenDuplicateDialog(sample.id)}
                                disabled={isReadOnly}
                              >
                                <FileCopyIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Verwijder veldmonster">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setSelectedSampleId(sample.id);
                                  setIsDeleteDialogOpen(true);
                                }}
                                disabled={isReadOnly}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={expandedItem === sample.id ? "Details inklappen" : "Details uitklappen"}>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => setExpandedItem(expandedItem === sample.id ? null : sample.id)}
                            >
                              {expandedItem === sample.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ListItem>
                      <Collapse in={expandedItem === sample.id} timeout="auto" unmountOnExit>
                        <VeldmonsterForm
                          sample={editableVeldmonsters.find(s => s.id === sample.id) || sample}
                          boreholes={boreholes}
                          analysemonsters={fetchedAnalysemonsters || []}
                          domainValues={domainValues}
                          domainValuesLoading={domainValuesLoading}
                          lengthUnitsData={lengthUnitsData}
                          lengthUnitsLoading={lengthUnitsLoading}
                          referentieVlakData={referentieVlakData}
                          referentieVlakLoading={referentieVlakLoading}
                          handleVeldmonsterChange={handleVeldmonsterChange}
                          isReadOnly={isReadOnly}
                        />
                      </Collapse>
                    </React.Fragment>
                  );
                })}
              </Box>
            );
          })}
        </List>
      )}
    </Box>
  );
}
